import { useEffect, useMemo, useState } from 'react';
import { Form } from 'antd';
import OrderService from "services/order"
import { useNavigate, useParams } from 'react-router';
import { useApp } from 'contexts/app';
import ProductVariantService from "services/product_variant"
import CustomerService from "services/customer"
import StoreSettingService from "services/store_setting"

import { useWorkSpace } from "contexts/workspace"
import _ from "lodash"
import '../table.css'
import { useWatch } from 'antd/es/form/Form';
import { OrderEditProvider } from './OrderEditContext';
import useIsMobile from '@/hooks/useIsMobile';
import OrderEditDesktop from './desktop';
import OrderEditMobile from './mobile';



const service = new OrderService();

const getInventoryErrorMessage = (error = {}, lineItems = []) => {
    const inventoryErrors = error?.inventory_errors ?? [];

    if (!_.isArray(inventoryErrors) || inventoryErrors.length === 0) {
        return null;
    }

    return (
        <div>
            <div style={{ marginBottom: 8 }}>
                Tồn kho không đủ cho một số sản phẩm. Bạn có thể kiểm tra lại số lượng hoặc tiếp tục lưu để bỏ qua kiểm tra tồn.
            </div>
            <ul style={{ paddingLeft: 20, margin: 0 }}>
                {inventoryErrors.map((item, index) => {
                    const line = lineItems.find(x => x.product_id == item.variant_id);
                    const label = line ? `${line.product?.name ?? "Sản phẩm"} ${line.variant?.variant_title ?? ""}`.trim() : item.variant_id;

                    if (item.reason === "insufficient_available") {
                        return (
                            <li key={`${item.variant_id}-${index}`}>
                                {label}: yêu cầu {item.requested ?? 0}, tồn khả dụng {item.available ?? 0}
                            </li>
                        );
                    }

                    if (item.reason === "variant_not_found") {
                        return (
                            <li key={`${item.variant_id}-${index}`}>
                                {label}: không tìm thấy biến thể hàng hóa
                            </li>
                        );
                    }

                    return (
                        <li key={`${item.variant_id}-${index}`}>
                            {label}: {item.reason ?? "Lỗi tồn kho"}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

const OrderDetail = () => {
    const isMobile = useIsMobile(992);
    const { showLoading, showNotify, showConfirm, message } = useApp();
    const [data, setData] = useState([]);
    const [lineItems, setLineItems] = useState([])
    const [form] = Form.useForm();
    const shipping_address = useWatch("shipping_address", form)
    //const customerPhone = Form.useWatch('username', form);
    const [products, setProducts] = useState([]);
    const [internalStatuses, setInternalStatuses] = useState([
        { id: "pending", title: "Chờ đóng gói" },
        { id: "packing", title: "Đã đóng gói" },
    ]);
    const [showEditF, setShowEdit] = useState({});
    const { workspace } = useWorkSpace();

    const navigate = useNavigate();

    useEffect(() => {
        loadProducts()
        loadInternalStatuses()
    }, [])


    const { id } = useParams();
    const selectedCustomerID = Form.useWatch("customer_id", form);
    //const id = null;
    useEffect(() => {
        loadData();
        setShowEdit({})
    }, [id, internalStatuses])

    useEffect(() => {
        loadProducts(selectedCustomerID);
    }, [selectedCustomerID])


    const getDefaultShippingAddress = (customer) => {
        const list = customer?.shipping_address ?? [];
        if (_.isArray(list) && list.length > 0) {
            const selected = list.find(x => x?.is_default) ?? list[0];
            return {
                full_name: selected?.full_name ?? customer?.full_name,
                address: selected?.address ?? customer?.address,
                phone_number: selected?.phone_number ?? customer?.phone,
            }
        }

        return {
            full_name: customer?.full_name,
            address: customer?.address,
            phone_number: customer?.phone,
        }
    }

    const onCustomerChange = async (cus) => {

        if (cus) {
            let customerDetail = cus;
            try {
                customerDetail = await new CustomerService().getByID(cus.id) ?? cus;
            } catch (error) {
                customerDetail = cus;
            }

            form.setFieldsValue({ shipping_address: getDefaultShippingAddress(customerDetail) })
        }
    }



    const loadProducts = async (customer_id) => {
        const service = new ProductVariantService();
        var lst = await service.list({
            customer_id: customer_id || undefined,
            fields: [
                "id",
                "variant_title",
                "original_price",
                "price_list",
                "product_id",
                "delete_on",
                "product.id",
                "product.name",
                "product.search_text",
                "product.delete_on",
                "image",
                "images",
                "product.thumbnail",
                "product.images"
            ]
        });
        const nextProducts = lst || [];
        setProducts(nextProducts);

        // Chỉ tính lại giá dòng hàng khi có khách hàng (pricing có thể áp dụng)
        // hoặc khi bỏ chọn khách (reset về giá gốc). Bỏ qua khi đơn chưa có dòng nào.
        if (_.isArray(lineItems) && lineItems.length > 0 && (customer_id || selectedCustomerID)) {
            const getPriceFromProducts = (variant_id, quantity) => {
                const product = nextProducts.find(x => x.id == variant_id);
                if (!product) return 0;
                if (_.isArray(product.price_list) && product.price_list.length > 0) {
                    const sortedPriceList = _.orderBy(product.price_list, o => o.min * -1);
                    const tier = sortedPriceList.find(x => quantity >= x.min);
                    if (tier) return tier.price;
                }
                return product.original_price ?? 0;
            };

            const nextLineItems = lineItems.map((line) => {
                const nextLine = { ...line };
                if (nextLine.price_type == "1") {
                    nextLine.price = getPriceFromProducts(nextLine.product_id, nextLine.quantity);
                }
                nextLine.amount = (nextLine.price ?? 0) * (nextLine.quantity ?? 0);
                return nextLine;
            });

            setLineItems(nextLineItems);
            const orderNew = calculateOrder(data, nextLineItems);
            setData(orderNew);
        }
    }

    const loadInternalStatuses = async () => {
        try {
            const statuses = await new StoreSettingService().getOrderInternalStatuses();
            if (_.isArray(statuses) && statuses.length > 0) {
                setInternalStatuses(statuses);
            }
        } catch (error) {
        }
    }

    const loadData = async () => {
        if (id == null) {
            const defaultInternalStatus = internalStatuses?.[0]?.id ?? "pending";
            var newOrder = {
                line_items: [], shipping_address: {}, customer: {},
                discount_amount: 0, shipping_amount: 0,

                payment_status: { id: "unpaid" },
                shipping_status: { id: "not_shipped" },
                internal_status: { id: defaultInternalStatus }

            };
            setData(newOrder);
            form.setFieldsValue({ ...newOrder })
            return;

        }
        showLoading(true);
        var order = await service.get({ id });
        setData(order);
        setLineItems(order.line_items);

        form.setFieldsValue({ ...order })
        //  form.setFieldsValue({customer_id: '11965cf1-71ee-414a-9276-5aaaf46d28a3'});

        showLoading(false);
    }

    const onFinish = values => {
        saveOrder(values)
    };
    const onFinishFailed = errorInfo => {
        console.log('Failed:', errorInfo);
    };

    const applyLineItemsUpdate = (updater) => {
        setLineItems((prevLines) => {
            const draftLines = (_.isArray(prevLines) ? prevLines : []).map((line) => ({ ...line }));
            const updatedLines = typeof updater === "function" ? updater(draftLines) : updater;
            const nextLines = calculateLineItems(updatedLines ?? []);

            setData((prevOrder) => calculateOrder(prevOrder, nextLines));
            return nextLines;
        });
    }

    const addProduct = (id) => {
        applyLineItemsUpdate((newLines) => {
            var old = newLines.find(x => x.product_id == id);
            if (old) {
                old.quantity++;
            }
            else {
                var price = calculateProductPrice(id, 1);
                var product = getProductByID(id);
                if (!product) return newLines;

                var line = {
                    product_id: id,
                    quantity: 1,
                    price: price,
                    amount: price,
                    product: product.product,
                    variant: product,
                    price_type: "1"
                }
                newLines.push(line)
            }

            return newLines;
        });
    }

    const calculateProductPrice = (id, quantity) => {
        var product = products.find(x => x.id == id);
        if (!product)
            return 0;
        if (_.isArray(product.price_list)) {
            if (product.price_list.length > 0) {

                var price_list = _.orderBy(product.price_list, o => o.min * -1)
                var price = price_list.find(x => quantity >= x.min);
                if (price) {
                    return price.price;
                }

            }
        }
        return product.original_price ?? 0;
    }

    const getProductByID = (id) => {
        var product = products.find(x => x.id == id);
        return product;
    }

    const onLineQuantityChange = (id, quantity) => {
        applyLineItemsUpdate((newLines) => {
            var old = newLines.find(x => x.product_id == id);
            if (old) {
                old.quantity = quantity;
                if (old.quantity <= 0) {
                    var index = newLines.findIndex(x => x.product_id == id);
                    newLines.splice(index, 1);
                }
            }

            return newLines;
        });
    }

    const onLinePriceChange = (id, price) => {
        applyLineItemsUpdate((newLines) => {
            var old = newLines.find(x => x.product_id == id);
            if (old) {
                old.price = price;
            }

            return newLines;
        });
    }




    const setLineItemPriceType = (id, type) => {
        applyLineItemsUpdate((newLines) => {
            var old = newLines.find(x => x.product_id == id);
            if (old) {
                old.price_type = type;
            }

            return newLines;
        });
    }

    const calculateLineItems = (lines) => {
        return (lines || []).map((sourceLine) => {
            var line = { ...sourceLine };
            if (line.price_type == "1") {
                line.price = calculateProductPrice(line.product_id, line.quantity)
            }
            line.amount = line.price * line.quantity;
            return line;
        });
    }

    const calculateOrder = (order, lines) => {
        var newOrder = { ...order };
        newOrder.sub_amount = _.sumBy(lines, x => x.amount);
        newOrder.total_item = _.sumBy(lines, x => x.quantity);
        newOrder.discount_amount = order.discount_amount ?? 0;
        newOrder.shipping_amount = order.shipping_amount ?? 0;
        newOrder.voucher_amount = order.voucher_amount ?? 0;
        newOrder.point_discount = order.point_discount ?? 0;
        newOrder.total_amount = newOrder.sub_amount - newOrder.discount_amount - newOrder.voucher_amount + newOrder.shipping_amount - newOrder.point_discount;
        return newOrder;
    }


    const saveOrder = async (info, options = {}) => {
        const { skipInventoryCheck = false } = options;

        showLoading(true)
        try {
            var newOrder = { ...info };
            var line = lineItems.map(x => {
                return ({
                    product_id: x.product_id,
                    quantity: x.quantity,
                    price: x.price,
                    amount: x.amount,
                    price_type: x.price_type
                })
            })
            newOrder.line_items = line;
            newOrder.discount_amount = data.discount_amount ?? 0;
            newOrder.shipping_amount = data.shipping_amount ?? 0;
            newOrder.voucher_amount = data.voucher_amount ?? 0;
            newOrder.voucher_code = data.voucher_code;
            newOrder.loyalty_point = data.loyalty_point;
            newOrder.point_discount = data.point_discount;
            if (skipInventoryCheck) {
                newOrder.skip_inventory_check = true;
            }

            if (info.id) {
                newOrder = await service.update(newOrder);
                showNotify("Đã lưu đơn hàng");
                loadData();
            } else {
                newOrder = await service.create(newOrder);
                showNotify("Đã tạo mới đơn hàng")
                navigate(`/order/edit/${newOrder.id}`)
            }
        } catch (error) {
            if (error?.error_code === 'ORDER_LIMIT_EXCEEDED') {
                showConfirm({
                    title: 'Giới hạn đơn hàng',
                    content: error.message,
                    okText: 'Nâng cấp gói',
                    cancelText: 'Đóng',
                    onOk: () => navigate('/account/subscription'),
                });
                return;
            }
            const hasInventoryError = !skipInventoryCheck && (_.isArray(error?.inventory_errors) ? error.inventory_errors.length > 0 : !!error?.inventory_error);
            if (hasInventoryError) {
                showConfirm({
                    title: "Tồn kho không đủ",
                    content: getInventoryErrorMessage(error, lineItems),
                    okText: "Tiếp tục lưu",
                    cancelText: "Đóng",
                    onOk: () => saveOrder(info, { skipInventoryCheck: true })
                });
                return;
            }

            message.error(error?.message || "Lưu đơn hàng thất bại");
        } finally {
            showLoading(false);
        }
    }


    const onDiscountAmountChange = (number) => {
        var newOrder = { ...data };
        newOrder.discount_amount = number;

        newOrder.shipping_amount = newOrder.shipping_amount ?? 0;
        newOrder.voucher_amount = newOrder.voucher_amount ?? 0;
        newOrder.total_amount = newOrder.sub_amount - newOrder.discount_amount - newOrder.voucher_amount + newOrder.shipping_amount - newOrder.point_discount;

        setData(newOrder);
    }

    const onShippingAmountChange = (number) => {
        var newOrder = { ...data };
        newOrder.shipping_amount = number ?? 0;
        newOrder.voucher_amount = newOrder.voucher_amount ?? 0;
        newOrder.discount_amount = newOrder.discount_amount ?? 0;;
        newOrder.total_amount = newOrder.sub_amount - newOrder.discount_amount - newOrder.voucher_amount + newOrder.shipping_amount - newOrder.point_discount;
        setData(newOrder);
    }

    const onLoyaltyPointChange = (number) => {
        var newOrder = { ...data };
        newOrder.loyalty_point = number ?? 0;
        newOrder.point_discount = number ?? 0;
        newOrder.voucher_amount = newOrder.voucher_amount ?? 0;
        newOrder.discount_amount = newOrder.discount_amount ?? 0;;
        newOrder.total_amount = newOrder.sub_amount - newOrder.discount_amount - newOrder.voucher_amount + newOrder.shipping_amount - newOrder.point_discount;
        setData(newOrder);
    }




    const onVoucherCodeChange = e => {
        var newOrder = { ...data };
        newOrder.voucher_code = e.target.value
        setData(newOrder);
    }

    const showEdit = (name) => {
        var newShow = { ...showEditF };

        newShow[name] = !newShow[name];
        setShowEdit(newShow)
    }

    const contextValue = useMemo(() => {
        return {
            addProduct,
            data,
            form,
            internalStatuses,
            lineItems,
            navigate,
            onCustomerChange,
            onDiscountAmountChange,
            onLinePriceChange,
            onLineQuantityChange,
            onLoyaltyPointChange,
            onShippingAmountChange,
            onVoucherCodeChange,
            products,
            saveOrder,
            onFinish,
            onFinishFailed,
            selectedCustomerID,
            setLineItemPriceType,
            shipping_address,
            showEdit,
            showEditF,
            workspace,
        };
    }, [
        data,
        form,
        internalStatuses,
        lineItems,
        navigate,
        products,
        selectedCustomerID,
        shipping_address,
        showEditF,
        workspace,
    ]);

    return (
        <OrderEditProvider value={contextValue}>
            {isMobile ? <OrderEditMobile /> : <OrderEditDesktop />}
        </OrderEditProvider>
    )
}

export default OrderDetail;
